import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { Paths } from '@/types/routes';
import { SearchConsoleProperty, GoogleConnectionStatus } from '@/services/searchConsole';
import { GA4Property } from '@/services/googleAnalytics';

type Props = {
  connectionStatus: GoogleConnectionStatus | null;
  availableSites: SearchConsoleProperty[];
  ga4Properties: GA4Property[];
  isLoadingConnection: boolean;
  isLoadingGa4: boolean;
  isCreating: boolean;
  onCreateProject: (data: {
    websiteUrl: string;
    name: string;
    siteUrl: string;
    ga4PropertyId: string;
    ga4PropertyName: string;
  }) => void;
};

const steps = ['Website URL', 'Google Account', 'Search Console', 'Analytics (Optional)'];

const ProjectOnboarding = function ({
  connectionStatus,
  availableSites,
  ga4Properties,
  isLoadingConnection,
  isLoadingGa4,
  isCreating,
  onCreateProject,
}: Props): JSX.Element {
  const [activeStep, setActiveStep] = useState(0);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [projectName, setProjectName] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedGa4, setSelectedGa4] = useState('');

  const isConnected = connectionStatus?.isConnected;

  const deriveProjectName = (url: string) => {
    const clean = url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
    setProjectName(clean);
    return clean;
  };

  const handleNext = () => {
    if (activeStep === 0 && websiteUrl) {
      if (!projectName) deriveProjectName(websiteUrl);
      // If google is already connected, skip step 1
      if (isConnected) {
        setActiveStep(2);
        // Auto-match site
        const domain = websiteUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
        const match = availableSites.find(
          (s) => s.siteUrl.includes(domain) || domain.includes(s.siteUrl.replace('sc-domain:', ''))
        );
        if (match) setSelectedSite(match.siteUrl);
      } else {
        setActiveStep(1);
      }
    } else if (activeStep === 1) {
      setActiveStep(2);
    } else if (activeStep === 2) {
      setActiveStep(3);
    } else if (activeStep === 3) {
      const ga4Prop = ga4Properties.find((p) => p.propertyId === selectedGa4);
      onCreateProject({
        websiteUrl,
        name: projectName || websiteUrl,
        siteUrl: selectedSite,
        ga4PropertyId: selectedGa4,
        ga4PropertyName: ga4Prop?.displayName ?? '',
      });
    }
  };

  const handleSkipGa4 = () => {
    onCreateProject({
      websiteUrl,
      name: projectName || websiteUrl,
      siteUrl: selectedSite,
      ga4PropertyId: '',
      ga4PropertyName: '',
    });
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <TrendingUpOutlinedIcon sx={{ fontSize: 40, color: '#054AD9', mb: 1 }} />
        <Typography variant="h5" fontWeight={700}>
          Create your first SEOx project
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Set up a project to start tracking your website&apos;s SEO performance.
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 0: Website URL */}
      {activeStep === 0 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 3, backgroundColor: '#fff' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            Enter your website URL
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. trio.dev"
            value={websiteUrl}
            onChange={(e) => {
              setWebsiteUrl(e.target.value);
              deriveProjectName(e.target.value);
            }}
            sx={{ mb: 2 }}
            size="small"
          />
          <TextField
            fullWidth
            label="Project Name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            sx={{ mb: 3 }}
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!websiteUrl}
            sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' }, textTransform: 'none' }}
          >
            Continue
          </Button>
        </Box>
      )}

      {/* Step 1: Connect Google */}
      {activeStep === 1 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 3, backgroundColor: '#fff' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            Connect Google Account
          </Typography>
          {isLoadingConnection ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ color: '#054AD9' }} />
            </Box>
          ) : !isConnected ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connect your Google account in Settings to access Search Console and Analytics data.
              </Typography>
              <Link to={Paths.SETTINGS_INTEGRATIONS} style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#4285F4', '&:hover': { backgroundColor: '#3367D6' }, textTransform: 'none', mb: 2 }}
                >
                  Connect Google in Settings
                </Button>
              </Link>
              <Box>
                <Button size="small" onClick={() => setActiveStep(2)} sx={{ textTransform: 'none' }}>
                  Skip for now
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2, fontSize: 13 }}>
                Google account is connected!
              </Alert>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' }, textTransform: 'none' }}
              >
                Continue
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Step 2: Select GSC Site */}
      {activeStep === 2 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 3, backgroundColor: '#fff' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            Select Search Console Site
          </Typography>
          {!isConnected || availableSites.length === 0 ? (
            <>
              <Alert severity="info" sx={{ fontSize: 13, mb: 2 }}>
                {!isConnected
                  ? 'Google is not connected. You can configure this later in project settings.'
                  : 'No Search Console sites found. You can add one later.'}
              </Alert>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' }, textTransform: 'none' }}
              >
                Continue
              </Button>
            </>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="" disabled>
                    Select a site...
                  </MenuItem>
                  {availableSites.map((site) => (
                    <MenuItem key={site.siteUrl} value={site.siteUrl}>
                      {site.siteUrl.replace('sc-domain:', '').replace(/^https?:\/\//, '')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' }, textTransform: 'none' }}
              >
                Continue
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Step 3: GA4 Property (Optional) */}
      {activeStep === 3 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 3, backgroundColor: '#fff' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Select GA4 Property (Optional)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            This enables traffic and user analytics on your project dashboard.
          </Typography>
          {isLoadingGa4 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : ga4Properties.length === 0 ? (
            <>
              <Alert severity="info" sx={{ fontSize: 13, mb: 2 }}>
                No GA4 properties found. You can configure this later.
              </Alert>
            </>
          ) : (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={selectedGa4}
                onChange={(e) => setSelectedGa4(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">None</MenuItem>
                {ga4Properties.map((prop) => (
                  <MenuItem key={prop.propertyId} value={prop.propertyId}>
                    {prop.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isCreating}
              sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' }, textTransform: 'none' }}
            >
              {isCreating ? 'Creating...' : 'Create Project'}
            </Button>
            <Button onClick={handleSkipGa4} disabled={isCreating} sx={{ textTransform: 'none' }}>
              Skip & Create
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProjectOnboarding;
